/** Graph X-axis title — tilt is angular degrees. */
export const GRAPH_TILT_AXIS_TITLE = 'Tilt (°)'

/** Default weld-position axis span (degrees). */
export const GRAPH_TILT_DEFAULT_MIN = 0
export const GRAPH_TILT_DEFAULT_MAX = 180

/** Padding beyond data extremes when they exceed the default 0–180° range. */
export const GRAPH_TILT_AXIS_PADDING = 5

/** Format a tilt value for axis ticks and tooltips. */
export function formatTiltLabel(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return String(Number(value.toFixed(2)))
}

/**
 * Unwrap raw tilt readings into a continuous degree progression for graphing.
 * When a lap starts past 180° (e.g. 352, 357), values are shifted so the wrap
 * maps through negatives: 352 → −8, 357 → −3, then 4 … 164.
 */
export function unwrapTilts(tilts: number[]): number[] {
  if (tilts.length === 0) return []
  const continuous = [tilts[0]]
  for (let i = 1; i < tilts.length; i++) {
    let curr = tilts[i]
    const prev = continuous[i - 1]
    while (curr - prev > 180) curr -= 360
    while (curr - prev < -180) curr += 360
    continuous.push(curr)
  }
  if (continuous[0] > GRAPH_TILT_DEFAULT_MAX) {
    return continuous.map((t) => t - 360)
  }
  return continuous
}

/**
 * X-axis bounds from unwrapped tilts: default 0–180; expand 5° past extremes
 * only when data falls outside that range. Also returns unpadded data extremes
 * for tick labeling (first label = dataMin).
 */
export function computeTiltAxisBounds(unwrappedTilts: number[]): {
  min: number
  max: number
  dataMin: number
  dataMax: number
} {
  if (unwrappedTilts.length === 0) {
    return {
      min: GRAPH_TILT_DEFAULT_MIN,
      max: GRAPH_TILT_DEFAULT_MAX,
      dataMin: GRAPH_TILT_DEFAULT_MIN,
      dataMax: GRAPH_TILT_DEFAULT_MAX
    }
  }
  const dataMin = Math.min(...unwrappedTilts)
  const dataMax = Math.max(...unwrappedTilts)
  return {
    min: dataMin < GRAPH_TILT_DEFAULT_MIN ? dataMin - GRAPH_TILT_AXIS_PADDING : GRAPH_TILT_DEFAULT_MIN,
    max: dataMax > GRAPH_TILT_DEFAULT_MAX ? dataMax + GRAPH_TILT_AXIS_PADDING : GRAPH_TILT_DEFAULT_MAX,
    dataMin,
    dataMax
  }
}

/** Tick step for a numeric degree axis (legacy / non-extended spans). */
export function graphTiltDegreeTickStep(min: number, max: number): number {
  const span = max - min
  if (span <= 30) return 5
  if (span <= 90) return 10
  return 15
}

/**
 * Whether data extends past the default 0–180° weld-position window
 * (wrap-start negatives or values above 180).
 */
export function isExtendedTiltAxis(dataMin: number, dataMax: number): boolean {
  return dataMin < GRAPH_TILT_DEFAULT_MIN || dataMax > GRAPH_TILT_DEFAULT_MAX
}

const GRAPH_TILT_TICK_STEP = 15
/** Skip extra overflow ticks closer than this to another label (−9 then −5 would overlap). */
const GRAPH_TILT_MIN_LABEL_GAP = 10
/** Do not label wrap starts of −4, −3, −2, −1 — only −5° and further left. */
const GRAPH_TILT_FIRST_LABEL_MAX = -5

function nearlyEqualDegree(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-9
}

function tooCloseForTiltLabel(a: number, b: number): boolean {
  return Math.abs(a - b) < GRAPH_TILT_MIN_LABEL_GAP - 1e-9
}

function pushUniqueDegreeTick(ticks: number[], value: number): void {
  const v = Number(value.toFixed(10))
  if (ticks.length === 0 || !nearlyEqualDegree(ticks[ticks.length - 1], v)) {
    ticks.push(v)
  }
}

/**
 * Build X tick values. Default 0–180 axes use 15° steps from 0.
 * Extended axes: first label is the wrap start when it is ≤ −5° (e.g. −9);
 * −4/−3/−2/−1 are not labeled. Then 0…180 at 15°. Last label is always 180.
 */
export function buildTiltDegreeTickValues(input: {
  dataMin: number
  dataMax: number
  axisMax: number
}): number[] {
  const { dataMin, dataMax, axisMax } = input
  if (!Number.isFinite(dataMin) || !Number.isFinite(axisMax)) return []

  if (!isExtendedTiltAxis(dataMin, dataMax)) {
    const step = graphTiltDegreeTickStep(GRAPH_TILT_DEFAULT_MIN, axisMax)
    const ticks: number[] = []
    for (let v = GRAPH_TILT_DEFAULT_MIN; v <= axisMax + 1e-9; v += step) {
      ticks.push(Number(v.toFixed(10)))
    }
    return ticks
  }

  const ticks: number[] = []

  if (dataMin < GRAPH_TILT_DEFAULT_MIN) {
    if (dataMin <= GRAPH_TILT_FIRST_LABEL_MAX) {
      pushUniqueDegreeTick(ticks, dataMin)
    }
    const overflowBelow: number[] = []
    for (let v = -GRAPH_TILT_TICK_STEP; v >= dataMin - 1e-9; v -= GRAPH_TILT_TICK_STEP) {
      overflowBelow.push(Number(v.toFixed(10)))
    }
    overflowBelow.reverse()
    for (const v of overflowBelow) {
      if (nearlyEqualDegree(v, dataMin)) continue
      if (tooCloseForTiltLabel(v, dataMin)) continue
      if (tooCloseForTiltLabel(v, GRAPH_TILT_DEFAULT_MIN)) continue
      pushUniqueDegreeTick(ticks, v)
    }
  }

  const mainMax = Math.min(GRAPH_TILT_DEFAULT_MAX, axisMax)
  for (let v = GRAPH_TILT_DEFAULT_MIN; v <= mainMax + 1e-9; v += GRAPH_TILT_TICK_STEP) {
    pushUniqueDegreeTick(ticks, v)
  }

  return ticks
}
